import React, { useState, useEffect } from 'react';
import {
  ArrowUpRight, ArrowDownLeft, Wallet,
  LogOut, CheckCircle2, Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import CheckInVerificationModal from '../components/CheckInVerificationModal';
import CheckOutModal from "../components/CheckOutModal";
import TopUpWalletModal from "../components/TopUpWalletModal";

// Local images
import user1 from '../assets/user1.png';
import user2 from '../assets/user2.png';
import user3 from '../assets/image.png';
import user4 from '../assets/user3.png';
import user5 from '../assets/user4.png';
import blankImage from '../assets/blank.png';

export default function DashboardPage() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [currentDate, setCurrentDate] = useState('');
  const [greeting, setGreeting] = useState('');
  const [guestData, setGuestData] = useState([]);
  const navigate = useNavigate();

  // Modal states
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const LOCAL_STORAGE_KEY = 'guestList';

  useEffect(() => {
    // Live Date & Greeting
    const date = new Date();
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    setCurrentDate(date.toLocaleDateString('en-US', options));

    const hour = date.getHours();
    if (hour < 12) setGreeting('Good Morning');
    else if (hour < 18) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');

    /*fetch('./data/guests.json')
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(data => {
          console.log(data)
          setGuestData(data.items); // Assuming your JSON has an "items" array
          setLoading(false);
        })
        .catch(error => {
          setError(error);
          setLoading(false);
        });*/

    const loadGuests = async () => {
      try {
        // Check localStorage first
        const savedGuests = localStorage.getItem(LOCAL_STORAGE_KEY);
        let guestData = savedGuests ? JSON.parse(savedGuests) : [];

        // 2. Check for pending guests (newly verified)
        const pendingGuests = localStorage.getItem("newGuest");
        if (pendingGuests) {
          const newGuests = JSON.parse(pendingGuests);
          guestData = [ ...newGuests, ...guestData]; // Merge
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(guestData));
          localStorage.removeItem("newGuest"); // Clear pending
        }

        // 3. Fallback to initial JSON if no data exists
        if (guestData.length === 0) {
          const response = await fetch('/data/guests.json');
          const defaultData = await response.json();
          guestData = defaultData.items;
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(guestData));
        }

        setGuestData(guestData);
      } catch (error) {
        console.error("Failed to load guests:", error);
      } finally {
        setLoading(false);
      }
    };

    loadGuests();
  }, []);

  // Handler to open Verify Guest modal
  const handleVerifyGuest = () => {
    setShowCheckInModal(true);
  };

  return (
    <div className="d-flex vh-100 overflow-hidden">
      {/* Single Sidebar Instance */}
      <Sidebar 
        active="dashboard"
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
      />

      {/* Main Content Area */}
      <div className="flex-grow-1 d-flex flex-column">
        <Header 
          darkMode={darkMode} 
          setDarkMode={setDarkMode} 
          toggleSidebar={() => setMobileSidebarOpen(!mobileSidebarOpen)}
          notifications={4}   
        />

        <main className="flex-grow-1 overflow-auto p-4 bg-light">
          {/* Greeting */}
          <div className="text-center mb-4">
            <p className="text-muted mb-1 fs-5">{currentDate}</p>
            <h2 className="greeting-text fw-bold">{greeting}, Admin 👋🏾</h2>
          </div>

          {/* Summary Cards */}
          <section className="row g-3 mb-5">
            <div className="col-md-4">
              <SummaryCard 
                title="Total Wallet Units" 
                value="503,200" 
                icon={<Wallet size={24} />} 
                iconBg="primary"
              />
            </div>
            <div className="col-md-4">
              <SummaryCard 
                title="Total Guests Verified" 
                value="128" 
                icon={<CheckCircle2 size={24} />} 
                iconBg="success"
              />
            </div>
            <div className="col-md-4">
              <SummaryCard 
                title="Pending Verifications" 
                value="6" 
                icon={<Clock size={24} />} 
                iconBg="warning"
              />
            </div>
          </section>

          {/* Quick Actions */}
          <section className="row g-3 mb-5">
            <div className="col-6 col-md-3">
              <QuickAction 
                icon={<ArrowUpRight size={18} />} 
                label="Check-In Guest" 
                onClick={handleVerifyGuest}  // open modal here too
              />
            </div>
            <div className="col-6 col-md-3">
              <QuickAction icon={<ArrowDownLeft size={18} />} label="Check-Out Guest" onClick={() => setShowCheckOutModal(true)} />
            </div>
            <div className="col-6 col-md-3">
              <QuickAction icon={<Wallet size={18} />} label="Top Up Wallet"  onClick={() => setShowTopUpModal(true)} outline />
            </div>
            <div className="col-6 col-md-3">
              <QuickAction icon={<Wallet size={18} />} label="Generate Report" outline />
            </div>
          </section>

          {/* Recent Verifications Slider */}
          <section className="mt-5">
            <div className="bg-white p-3 rounded border" style={{ width: '100%' }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold m-0">Recent Verifications</h5>
                <button className="btn btn-sm btn-outline-primary">See All</button>
              </div>

              <div className="position-relative" style={{ width: '100%' }}>
                <button 
                  className="btn btn-light position-absolute top-50 start-0 translate-middle-y shadow-sm"
                  style={{ zIndex: 2 }}
                  onClick={() => scrollSlider('left')}
                >
                  ‹
                </button>

                <div 
                  id="verifications-slider" 
                  className="d-flex overflow-hidden gap-3 pb-2" 
                  style={{ scrollBehavior: 'smooth' }}
                >
                  {guestData.map((user, index) => (
                    <div 
                      key={index} 
                      className="card border flex-shrink-0"
                      style={{
                        width: 'calc((100% - (5 * 0.75rem)) / 5)', 
                        minWidth: '180px'
                      }}
                    >
                      <div 
                        className="mx-auto mt-3 rounded overflow-hidden" 
                        style={{ width: '120px', height: '120px', background: '#d5d5d5ff' }}
                      >
                        <img 
                          src={user.img ? user.img : blankImage }
                          alt={user.name} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>

                      <div className="card-body text-center">
                        <h6 className="fw-bold">{user.name}</h6>
                        <p className="small text-muted mb-1">{user.date}</p>
                        <p className="small text-muted mb-1"><strong>Method:</strong> {user.method}</p>
                        <span className={`badge bg-${getStatusColor(user.status)} mb-2`}>
                          {user.status}
                        </span>
                        {/*<button className="btn btn-primary btn-sm w-100 mt-2">View Guest</button>*/}
                      </div>
                    </div>
                  ))}
                </div>

                <button 
                  className="btn btn-light position-absolute top-50 end-0 translate-middle-y shadow-sm"
                  style={{ zIndex: 2 }}
                  onClick={() => scrollSlider('right')}
                >
                  ›
                </button>
              </div>
            </div>
          </section>

        </main>
      </div>

      {/* Modals */}
      <CheckInVerificationModal 
        show={showCheckInModal} 
        onClose={() => setShowCheckInModal(false)} 
      />
      <CheckOutModal 
        show={showCheckOutModal} 
        onClose={() => setShowCheckOutModal(false)} 
      />
      <TopUpWalletModal 
        show={showTopUpModal} 
        onClose={() => setShowTopUpModal(false)} 
      />
    </div>
  );
}

/* SummaryCard and QuickAction components unchanged */

function SummaryCard({ title, value, icon, iconBg }) {
  return (
    <div className="p-3 rounded bg-white d-flex align-items-center gap-3" style={{ border: '1px solid #eee' }}>
      <div className={`d-flex align-items-center justify-content-center rounded-circle bg-${iconBg}-subtle`} 
           style={{ width: '50px', height: '50px' }}>
        <span className={`text-${iconBg}`}>{icon}</span>
      </div>
      <div>
        <p className="text-muted small mb-1">{title}</p>
        <h4 className="fw-bold m-0">{value}</h4>
      </div>
    </div>
  );
}

function QuickAction({ icon, label, outline, onClick }) {
  return (
    <button 
      className={`btn w-100 py-3 d-flex align-items-center justify-content-center gap-2 ${outline ? 'btn-outline-primary' : 'btn-primary'}`}
      onClick={onClick}  
      type="button"
    >
      {icon} {label}
    </button>
  );
}

function getStatusColor(status) {
  switch (status) {
    case 'Verified': return 'success';
    case 'Failed': return 'warning';
    case 'Flagged': return 'danger';
    default: return 'secondary';
  }
}

function scrollSlider(direction) {
  const slider = document.getElementById('verifications-slider');
  const scrollAmount = 250;
  slider.scrollLeft += direction === 'left' ? -scrollAmount : scrollAmount;
}
